// The one answer to "should we offer this customer add-ons?"
// (025-subscription-addons).
//
// This predicate used to be written out in every component that shows an add-on
// CTA — fourteen files, three spellings — and the copies had already disagreed:
// some checked only whether the SUBSCRIPTION could hold add-ons and not whether
// this USER may buy them, so a Team member with an editor role was invited to
// "add more Kits to your current plan" and then met the add-ons page's
// door-closed screen. Every entry point now derives from here, so the two halves
// cannot drift apart again.
//
// Lives beside `addOnResources.js` rather than in `app/mixins.js` because it
// composes that module's `isSupportedFor` / `isUnlimitedFor`, and the components
// that need it already import from this directory.

import { addOnSectionRoute, isSupportedFor, isUnlimitedFor } from './addOnResources'

// Whether the SUBSCRIPTION can hold add-ons at all: active and Stripe-billed
// (`AddOns.eligible?/1` server-side, serialized as `add_ons_allowed`).
// Plan-agnostic, and says nothing about who is asking.
//
// Exported as a function as well as through the mixin below for the components
// that take their subscription as a prop rather than reading the store.
export function addOnsAllowedFor(subscription) {
  return subscription?.addOnsAllowed === true
}

export const AddOnEligibilityMixin = {
  computed: {
    addOnsAllowed() {
      return addOnsAllowedFor(this.$subscription)
    },

    // `$can.billingPortal.create`, safely: `$can` resolves to
    // `canByRole[role] || {}` (`plugins/shortcuts.js`), so `billingPortal` is
    // undefined for an unmapped role and before policies load, and a bare
    // `.create` on it throws during render.
    //
    // It has to be a computed rather than a `?.` written inline in the markup:
    // template expressions are compiled by buble
    // (`vue-template-es2015-compiler`), which cannot parse optional chaining and
    // fails the build outright. Templates read this instead.
    canManageBilling() {
      return Boolean(this.$can.billingPortal?.create)
    },

    // Whether THIS user may actually buy add-ons. `AddOnsView` turns away anyone
    // without `billingPortal.create`, so a CTA that skips this half is an
    // invitation to a page that refuses them.
    canPurchaseAddOns() {
      return this.addOnsAllowed && this.canManageBilling
    }
  },

  methods: {
    // Whether the add-ons page would actually sell more of `resource`: the user
    // can buy, the plan uses the resource at all (bandwidth is meaningless
    // without a private registry), and the baseline isn't already unlimited —
    // the server short-circuits an unlimited baseline and never adds purchased
    // units to it, so a purchase there buys nothing.
    canAddOnFor(resource) {
      return this.canPurchaseAddOns && isSupportedFor(this.$subscription, resource) && !isUnlimitedFor(this.$subscription, resource)
    },

    // Re-exposed so templates can build the link without each one spelling out
    // the route name and `?section=` again.
    addOnSectionRoute
  }
}
